import { Menu } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router";

import { AppSidebar, SidebarEdgeToggle } from "@/components/app-sidebar";
import { BrandLogo } from "@/components/brand-logo";
import { EnvironmentBadge } from "@/components/environment-badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ViewAsBar } from "@/components/view-as-bar";
import { useOutsideDismiss } from "@/hooks/use-outside-dismiss";
import { useConnectorOverview, useMe } from "@/lib/api";
import { APP_NAME } from "@/lib/app-info";
import { logger } from "@/lib/logger";
import { focusMainContent, MAIN_CONTENT_ID } from "@/lib/main-content";
import {
  setSidebarChoice,
  type SidebarLayout,
  useSidebarCollapsed,
  useSidebarLayout,
} from "@/lib/sidebar";
import { cn } from "@/lib/utils";
import { VIEW_AS_LAYOUT } from "@/lib/view-as";

/**
 * The frame of every signed-in page, as a layout route. From 1024 px wide the
 * sidebar sits beside the page, full or collapsed to a rail of icons by the
 * round handle on its right border, level with the logo (remembered in the
 * browser; collapsed below 1280 px until someone expands it). From 640 px to
 * 1023 px the rail is always there, and that handle opens the full sidebar
 * over the page. On a phone, below 640 px, a slim top bar holds the logo and
 * a menu button that opens the full sidebar in a sheet.
 * The widths live in `src/lib/sidebar.ts`. The shell stays mounted while
 * people move between pages, so only the page (`<Outlet />`) changes.
 *
 * De Ceuster's logo leads at the top of the sidebar; Velta's credit appears
 * only on the sign-in screen and under Settings → About.
 *
 * While a developer views the dashboard as someone else (`/auth/me` carries
 * `view_as`), `<ViewAsBar>` spans the window above it all, and the sidebar
 * and the phone's top bar stick under it (`VIEW_AS_LAYOUT`). Without a view
 * nothing of that renders, and the shell is as it always was.
 */
export function AppShell() {
  const layout = useSidebarLayout();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const view = useMe().data?.view_as ?? null;
  const viewing = view !== null;

  // A new page starts at its top (the document scrolls, and the router keeps
  // the position otherwise); a link to a `#section` is left to the browser.
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
    logger.debug("[nav]", pathname);
  }, [pathname]);

  return (
    <>
      {/* First in the tab order, before the bar, and drawn over it when it shows. */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        onClick={(event) => {
          event.preventDefault();
          focusMainContent();
        }}
        className={cn(
          "bg-card text-primary focus-visible:ring-ring sr-only z-50 rounded-md px-4 py-2 text-sm font-semibold focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus-visible:ring-2 focus-visible:outline-none",
          viewing && "z-[60]",
        )}
      >
        {t("shell.skipToContent")}
      </a>
      {view && <ViewAsBar view={view} />}
      <div className={cn("bg-background flex min-h-screen", viewing && VIEW_AS_LAYOUT.shell)}>
        {layout !== "phone" && <SidebarColumn layout={layout} viewing={viewing} />}
        <div className="flex min-w-0 flex-1 flex-col">
          {layout === "phone" && <PhoneBar viewing={viewing} />}
          <main id={MAIN_CONTENT_ID} tabIndex={-1} className="flex-1 focus:outline-none">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}

interface SidebarColumnProps {
  /** Beside the page, or the rail whose button opens the sidebar over the page. */
  layout: Exclude<SidebarLayout, "phone">;
  /** A view is on: the column sticks under the view-as bar, as much shorter. */
  viewing?: boolean;
}

/**
 * The sidebar's column, left of the page and as tall as the window.
 *
 * Beside the page (`docked`) the column itself grows from the rail to the full
 * sidebar and back, and the page makes room; the choice is remembered.
 *
 * As a rail (`rail`) the column stays one icon wide, and expanding widens only
 * the panel inside it, which floats over the page and moves nothing. The panel
 * is open for this visit only. It closes on the handle (which rides its edge
 * out and back), on any navigation, on Escape, on a press outside it and when
 * focus leaves it; the handle's tooltip shows in the rail only, so one Escape
 * always closes it. Escape hands focus back to the handle; a chosen link
 * keeps it, in the same place in the rail; a press or a Tab outside leaves it
 * where the person went. It never traps focus.
 *
 * The handle (`SidebarEdgeToggle`) sits in the element that carries the
 * border, before the sidebar: outside the sidebar's scroll container, so
 * nothing clips its overhang, and first in the panel's tab order.
 *
 * Both are the same elements, so crossing 1024 px never remounts the sidebar.
 */
function SidebarColumn({ layout, viewing = false }: SidebarColumnProps) {
  const docked = layout === "docked";
  const dockedCollapsed = useSidebarCollapsed();
  const { pathname } = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const sidebarId = useId();

  // Close the panel on any route change, also one the sidebar's own links did
  // not make (the browser's back button), and whenever the window changes
  // layout, so a narrow window always starts as the rail. Adjusted during render.
  const [lastPathname, setLastPathname] = useState(pathname);
  const [lastLayout, setLastLayout] = useState(layout);
  if (pathname !== lastPathname || layout !== lastLayout) {
    setLastPathname(pathname);
    setLastLayout(layout);
    setPanelOpen(false);
  }

  const open = !docked && panelOpen;
  const dismissProps = useOutsideDismiss(open, (reason) => {
    logger.debug("[nav]", "sidebar panel closed:", reason);
    setPanelOpen(false);
    if (reason === "escape") {
      toggleRef.current?.focus();
    }
  });

  const collapsed = docked ? dockedCollapsed : !open;
  const toggle = () => {
    if (docked) {
      setSidebarChoice(dockedCollapsed ? "expanded" : "collapsed");
    } else {
      logger.debug("[nav]", "sidebar panel", open ? "closed" : "open");
      setPanelOpen(!open);
    }
  };

  return (
    <div
      className={cn(
        // Above the page, so the panel covers it (the page's own positioned
        // pieces included); below the menus, tooltips and dialogs (z-50).
        "sticky top-0 z-40 h-screen shrink-0 transition-[width] duration-200 ease-out motion-reduce:transition-none",
        // Under the view-as bar while a view is on, as much shorter.
        viewing && cn(VIEW_AS_LAYOUT.stickyTop, VIEW_AS_LAYOUT.sidebarHeight),
        // The rail fits one icon centred under the logo mark: 4rem, where every icon keeps its place.
        docked && !dockedCollapsed ? "w-64" : "w-16",
      )}
    >
      <div
        {...dismissProps}
        className={cn(
          // Positioned, so the handle rides this element's right border.
          "border-sidebar-border h-full border-r",
          docked
            ? "relative"
            : "absolute inset-y-0 left-0 transition-[width] duration-200 ease-out motion-reduce:transition-none",
          !docked && (open ? "w-64 shadow-md" : "w-16"),
        )}
      >
        <SidebarEdgeToggle
          collapsed={collapsed}
          onToggle={toggle}
          controls={sidebarId}
          buttonRef={toggleRef}
          tooltip={!open}
        />
        <AppSidebar
          id={sidebarId}
          collapsed={collapsed}
          onNavigate={docked ? undefined : () => setPanelOpen(false)}
        />
      </div>
    </div>
  );
}

/**
 * The phone's top bar: the menu button, which opens the sidebar in a sheet
 * from the left, the logo, and outside production the environment's badge,
 * so a test environment shows without opening the menu. The badge is in its
 * full form, with "Alleen lezen" written out: a tooltip never opens on a
 * touch screen, so the compact form would hide that from sighted people. On a
 * narrow phone those words wrap under the name rather than being cut off, and
 * the menu button and the logo keep their size. The
 * sheet traps focus while open and closes on Escape, on the overlay, and on
 * any navigation; after a navigation focus moves to the new page's content
 * rather than back to the menu button. While a view is on, it sticks under
 * the view-as bar (`viewing`).
 */
export function PhoneBar({ viewing = false }: { viewing?: boolean }) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  // The sidebar's query, read here as well: the sheet's sidebar mounts only when it opens.
  const { data: overview } = useConnectorOverview();
  const [open, setOpen] = useState(false);
  // Set when a choice in the sheet leaves for a page, so closing hands focus
  // to the content instead of the menu button.
  const navigatedRef = useRef(false);

  // Close on any route change, also one the sheet's own links did not make
  // (the browser's back button), by adjusting state during render.
  const [lastPathname, setLastPathname] = useState(pathname);
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setOpen(false);
  }

  return (
    <header
      className={cn(
        "border-border bg-card sticky top-0 z-30 flex items-center gap-2 border-b px-3 py-2",
        viewing && VIEW_AS_LAYOUT.stickyTop,
      )}
    >
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("shell.openMenu")}
            // Never squeezed by the badge beside the logo: it keeps its full tap area.
            className="shrink-0"
          >
            <Menu className="size-5" aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-72 gap-0 p-0 sm:max-w-72"
          onCloseAutoFocus={(event) => {
            if (navigatedRef.current) {
              navigatedRef.current = false;
              event.preventDefault();
              focusMainContent();
            }
          }}
        >
          <SheetTitle className="sr-only">{t("shell.menu")}</SheetTitle>
          <SheetDescription className="sr-only">{t("shell.menuDescription")}</SheetDescription>
          <AppSidebar
            onNavigate={() => {
              navigatedRef.current = true;
              setOpen(false);
            }}
          />
        </SheetContent>
      </Sheet>
      <Link
        to="/home"
        aria-label={t("shell.homeLink", { app: APP_NAME })}
        className="focus-visible:ring-ring shrink-0 rounded-sm focus-visible:ring-2 focus-visible:outline-none"
      >
        <BrandLogo variant="compact" className="h-7" />
      </Link>
      {/* Where the bar is too narrow for one line, "Alleen lezen" wraps under the name. */}
      <EnvironmentBadge overview={overview} className="h-auto flex-wrap gap-y-0.5" />
    </header>
  );
}
